import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cleanerApi, getCleanerToken, setCleanerToken, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// Dedicated route (not a modal) so a temporary-password cleaner is fully
// locked out of the dashboard/bookings until they set their own password.
// Reached either:
//  (a) right after login, when POST /cleaners/login returns must_change_password:true
//  (b) via a redirect guard (dashboard load, or a 403 must_change_password from any
//      cleaner action endpoint, caught by the cleanerApi interceptor)
//  (c) voluntarily, from the "Change password" button in the dashboard's Account & security card
export default function CleanerChangePasswordPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const L = (en, es) => (locale === "es" ? es : en);

  const [checking, setChecking] = useState(true);
  const [forced, setForced] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const checkStatus = useCallback(async () => {
    if (!getCleanerToken()) { navigate("/cleaner/login"); return; }
    try {
      const r = await cleanerApi.get("/cleaner/me");
      const data = unwrap(r);
      setForced(!!data?.user?.must_change_password);
    } catch {
      setCleanerToken(null);
      navigate("/cleaner/login");
    } finally {
      setChecking(false);
    }
  }, [navigate]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const logout = () => { setCleanerToken(null); navigate("/cleaner/login"); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      toast.error(L("New password must be at least 8 characters.", "La nueva contraseña debe tener al menos 8 caracteres."));
      return;
    }
    if (!/[A-Za-z]/.test(form.next) || !/\d/.test(form.next)) {
      toast.error(L("New password must include a letter and a number.", "La nueva contraseña debe incluir una letra y un número."));
      return;
    }
    if (form.next !== form.confirm) {
      toast.error(L("Passwords don't match.", "Las contraseñas no coinciden."));
      return;
    }
    setSaving(true);
    try {
      await cleanerApi.post("/cleaner/change-password", { current_password: form.current, new_password: form.next });
      toast.success(L("Password updated", "Contraseña actualizada"));
      navigate("/cleaner/dashboard", { replace: true });
    } catch (e2) {
      const msg = e2?.response?.data?.detail?.message || e2?.message || L("Could not update password", "No pudimos actualizar la contraseña");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-[#FAFAF9]"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="cleaner-change-password-page">
      <SEO title={L("Change password — Ever Clean Home", "Cambiar contraseña — Ever Clean Home")} noindex />
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brand/logo-everclean-wordmark.png" alt="Ever Clean Home" className="h-11 md:h-14 w-auto object-contain" />
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-sm font-medium text-[#4B5563] hover:text-[#0EA5A4]" data-testid="cleaner-logout">
            <LogOut className="w-4 h-4" /> {L("Log out", "Salir")}
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-14">
        {forced && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 mb-6 flex items-start gap-3" data-testid="password-change-required-banner">
            <span className="w-9 h-9 rounded-xl bg-amber-200 grid place-items-center shrink-0">
              <ShieldCheck className="w-4.5 h-4.5 text-amber-700" />
            </span>
            <div className="text-sm text-amber-900">
              {L("Welcome to Ever Clean Home! Please set your own password before continuing.",
                 "¡Bienvenido a Ever Clean Home! Por favor configura tu propia contraseña antes de continuar.")}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E7EB]">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-5 h-5 text-[#0EA5A4]" />
            <h1 className="font-display text-2xl font-extrabold tracking-tighter">{L("Change password", "Cambiar contraseña")}</h1>
          </div>
          <p className="text-sm text-[#4B5563] mt-1">
            {forced
              ? L("For your security, please set your own password to unlock your dashboard.",
                  "Por seguridad, configura tu propia contraseña para desbloquear tu panel.")
              : L("Pick a strong password you don't use anywhere else.",
                  "Elige una contraseña fuerte que no uses en otro lado.")}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs">{L("Current password", "Contraseña actual")}</Label>
              <Input type="password" value={form.current} onChange={(e) => set({ current: e.target.value })} className="rounded-xl mt-1" required data-testid="current-password" />
            </div>
            <div>
              <Label className="text-xs">{L("New password", "Nueva contraseña")}</Label>
              <Input type="password" value={form.next} onChange={(e) => set({ next: e.target.value })} className="rounded-xl mt-1" required data-testid="new-password" />
              <p className="text-[11px] text-[#9CA3AF] mt-1">{L("At least 8 characters, with a letter and a number.", "Al menos 8 caracteres, con una letra y un número.")}</p>
            </div>
            <div>
              <Label className="text-xs">{L("Confirm new password", "Confirmar nueva contraseña")}</Label>
              <Input type="password" value={form.confirm} onChange={(e) => set({ confirm: e.target.value })} className="rounded-xl mt-1" required data-testid="confirm-password" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold" data-testid="update-password-btn">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : L("Set new password", "Configurar nueva contraseña")}
            </Button>
            {!forced && (
              <button type="button" onClick={() => navigate("/cleaner/dashboard")} className="w-full text-center text-sm text-[#4B5563] hover:text-[#0EA5A4]" data-testid="change-password-back-link">
                {L("Back to dashboard", "Volver al panel")}
              </button>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
